import React, { useState } from "react";
import styled from "styled-components";
import { useHistory, useLocation } from "react-router-dom";
import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu";
import FA from "@fortawesome/react-fontawesome";
import { faCheck, faQuestionCircle, faExternalLinkAlt } from "@fortawesome/fontawesome-free-solid";
import { H3, SmallBody } from "../TextStyles";
import { Formik } from "formik";
import { Flex } from "grid-styled";
import { RadioGroup } from "../Forms";
import yup from "yup";
import { withProps } from "recompose";
import calculateAvailableOptions from "../../calculateAvailableOptions";
import removeInvalidSelections from "../../removeInvalidSelections";
import { buildManufactureCode, selectFirstValidFromSelection } from "../../manufactureCode";
import getImageNames from "../../getImageNames";
import Overlay from "../Overlay";
import DescriptionList from "../DescriptionList";
import seatComponents from "../../seatComponents";
import notifications from "../notifications";
import { Box } from "grid-styled";
import SVGIcon from "../SVGIcon";
import powderCoatImage from "./images/powderCoat.jpeg";

const { REACT_APP_IMAGES_URL: imageUrl } = process.env;
const visibleOptions = [
  "seat",
  "back",
  "performance",
  "base",
  "height",
  "casters",
  "control",
  "footring",
  "arm",
  "upholstery",
];
const useLowerImage = ["base", "height", "casters", "footring", "performance"];

const UpholsteryLink = styled.a`
  display: inline-block;
  font-size: 0.8rem;
  margin-left: 1rem;
`;

const ItemCheck = styled.div`
  position: absolute;
  z-index: 1;
  width: 44%;
  height: 44%;
  top: 28%;
  left: 28%;
  justify-content: center;
  align-items: center;
  font-size: 2.3rem;
  padding-top: 0.2rem;
  display: ${({ isSelected }) => (isSelected ? "flex" : "none")};
  color: ${({ isSelected }) => (isSelected ? "white" : "#CCC")};
  border-radius: 50%;
  background-color: ${({ theme }) => theme.primary};
`;

const ItemStyle = styled.div`
  width: 130px;
  display: inline-block;
  font-size: 14px;
  margin-right: 10px;
  cursor: pointer;

  &:hover ${ItemCheck} {
    display: flex;
  }
`;

const ItemImg = styled.div`
  width: 125px;
  height: 125px;
  background-image: url(${({ thumb }) => thumb});
  background-size: cover;
  background-position-y: ${({ lower }) => lower && "-75px"};
  background-color: ${({ theme, hexColor }) => hexColor || theme.gray};
  position: relative;
  border-radius: ${({ round }) => (round ? "100%" : "0")};
`;

const ScrollStyle = styled.div`
  & .react-horizontal-scrolling-menu--wrapper {
    position: relative;
  }
  & .react-horizontal-scrolling-menu--scroll-container {
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const LeftScroll = styled.div`
  position: absolute;
  width: 40px;
  height: 100%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  background-color: ${({ theme }) => theme.primary};
`;

const RightScroll = styled(LeftScroll)`
  right: 0;
`;

const LeftArrow = () => {
  const { isFirstItemVisible, scrollPrev } = React.useContext(VisibilityContext);
  if (isFirstItemVisible) return null;
  return (
    <LeftScroll onClick={() => scrollPrev()}>
      <SVGIcon icon="arrow-left" />
    </LeftScroll>
  );
};

const RightArrow = () => {
  const { isLastItemVisible, scrollNext } = React.useContext(VisibilityContext);
  if (isLastItemVisible) return null;
  return (
    <RightScroll onClick={() => scrollNext()}>
      <SVGIcon icon="arrow-right" />
    </RightScroll>
  );
};

const notifyOnAutoChange = ({ changed, selected }) => {
  changed.forEach(({ key, value }) => {
    const { data, name } = seatComponents.find(({ type }) => type === key);
    const newOption = data.find(({ code }) => code === value);
    const newOptionName = newOption.dynamicName ? newOption.dynamicName(selected) : newOption.name;
    notifications({ warning: `${name} has automatically changed to ${newOptionName}` });
  });
};

const selectOption = ({ history, location, currentSelected, option, item }) => {
  let selected = currentSelected;

  selected[option.type] = item.code;

  const beforeAutoChange = { ...selected };

  selected = removeInvalidSelections(selected, option.type);

  selected = selectFirstValidFromSelection(selected, { forceType: option.type });

  // need to reset this in fringe cases where the component isn't available until running through all component
  // options, and it doesn't actually change what the user selected.
  selected[option.type] = item.code;

  // selected = detectSeries(selected);

  const changed = [];
  Object.entries(selected).forEach(([key, value]) => {
    if (beforeAutoChange[key] !== value) changed.push({ key, value });
  });

  if (changed.length > 0) {
    notifyOnAutoChange({ changed, selected });
  }

  const manufactureCode = buildManufactureCode(selected);
  location.pathname = `/build/${manufactureCode}`;
  // location.search = queryString.stringify({
  //   ...queryString.parse(location.search),
  //   series: selected.series,
  // });
  history.replace(location);
};

const Item = ({ option, item, selected: currentSelected }) => {
  const history = useHistory();
  const location = useLocation();

  let asIfItemWasSelected = { ...currentSelected };
  asIfItemWasSelected[option.type] = item.code;

  asIfItemWasSelected = removeInvalidSelections(asIfItemWasSelected, option.type);

  asIfItemWasSelected = selectFirstValidFromSelection(asIfItemWasSelected, {
    forceType: option.type,
  });

  asIfItemWasSelected[option.type] = item.code;

  const { upper, lower } = getImageNames(asIfItemWasSelected);

  let useLower = useLowerImage.includes(option.type);

  // AJ series stools only have lower images
  if (currentSelected?.series === "AJ" || item?.code === "TX") useLower = true;

  const imageName = useLower ? lower : upper;

  const isSelected = currentSelected[option.type] === item.code;

  return (
    <ItemStyle onClick={() => selectOption({ history, location, currentSelected, option, item })}>
      <ItemImg
        lower={useLower}
        thumb={option.type === "powderCoat" ? null : `${imageUrl}${imageName}-000.png`}
        hexColor={item.hexColor}
        round={option.type === "powderCoat"}
      >
        <ItemCheck isSelected={isSelected}>
          <FA icon={faCheck} />
        </ItemCheck>
      </ItemImg>
      <SmallBody>{item.name}</SmallBody>
    </ItemStyle>
  );
};

const Option = ({ option, selected, setOpenDescription }) => {
  const { type, name } = option || {};

  if (!visibleOptions.includes(type)) return null;

  const data = option.data.map((d) => ({
    ...d,
    name: d.dynamicName ? d.dynamicName(selected) : d.name,
  }));

  return (
    <div>
      <H3 mb="1rem">
        {name} Options{" "}
        <FA
          style={{ cursor: "pointer" }}
          icon={faQuestionCircle}
          onClick={() => setOpenDescription(type)}
        />
        {selected?.upholstered === "upholstered" && type === "upholstery" && (
          // eslint-disable-next-line react/jsx-no-target-blank
          <UpholsteryLink
            target="_blank"
            href="https://select.cfstinson.com/Finishes/Samples.jsp?lid=6420"
          >
            <FA icon={faExternalLinkAlt} /> View More Options
          </UpholsteryLink>
        )}
      </H3>

      <ScrollStyle>
        <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow} onWheel={onWheel}>
          {data.map((item) => (
            <Item
              key={item.code}
              itemId={item.code}
              option={option}
              item={item}
              selected={selected}
            />
          ))}
        </ScrollMenu>
      </ScrollStyle>
    </div>
  );
};

const simpleSchema = yup.object().shape({
  powderCoat: yup.string().label("Powder Coat"),
  vinylUnderwrap: yup.string().label("Vinyl Underwrap"),
  memoryFoam: yup.string().label("Memory Foam"),
});

const RadioField = withProps({ schema: simpleSchema })(RadioGroup);

const SimpleOptions = ({ selected, options, setOpenDescription }) => {
  const history = useHistory();
  const location = useLocation();

  const showOptions = ["vinylUnderwrap", "memoryFoam"];

  const simpleOptions = options.filter(({ type }) => showOptions.includes(type));

  return (
    <Formik
      initialValues={{
        vinylUnderwrap: selected.vinylUnderwrap,
        memoryFoam: selected.memoryFoam,
        powderCoat: selected.powderCoat,
      }}
      validationSchema={simpleSchema}
      onSubmit={() => null}
      component={() => (
        <React.Fragment>
          {simpleOptions.map((option) => {
            const opt = option.data.map(({ code, name }) => {
              return { id: code, name };
            });

            return (
              <Box key={option.type} my="2rem">
                <RadioField
                  key={option.type}
                  name={option.type}
                  options={opt}
                  label={
                    <H3>
                      {option.name}{" "}
                      <FA
                        style={{ cursor: "pointer" }}
                        icon={faQuestionCircle}
                        onClick={() => setOpenDescription(option.type)}
                      />
                    </H3>
                  }
                  afterChange={(event) => {
                    selectOption({
                      history,
                      location,
                      currentSelected: selected,
                      option,
                      item: { code: event.target.value },
                    });
                  }}
                />
              </Box>
            );
          })}
        </React.Fragment>
      )}
    />
  );
};

const OverlayContainer = styled.div`
  background: white;
  padding: 1rem;
  text-align: left;
`;

const Close = styled.span`
  cursor: pointer;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.primary};
  @media print {
    display: none;
  }
`;

const powderCoatTypeSchema = yup.object().shape({
  powderCoatType: yup.string().label("Powder Coat Type"),
});

const PowderCoatTypeField = withProps({ schema: powderCoatTypeSchema })(RadioGroup);

const PowderCoatOptions = ({ selected, options, setOpenDescription }) => {
  const history = useHistory();
  const location = useLocation();
  const [openPowderCoatExample, setOpenPowderCoatExample] = useState(false);
  const [showPowderCoatColors, setShowPowderCoatColors] = useState(
    selected.powderCoat !== "none" &&
      selected.powderCoat !== undefined &&
      selected.powderCoat !== "X"
  );

  const powderCoatOption = options.filter(({ type }) => type === "powderCoat")[0];
  const powderCoatOptions = powderCoatOption?.data;

  if (
    powderCoatOptions.length === 0 ||
    (powderCoatOptions[0]?.code === "X" && powderCoatOptions.length === 1)
  )
    return null;

  // const powderCoatOption = powderCoatOptions[0];
  const manufactureCode = buildManufactureCode(selected);

  const handlePowderCoatTypeChange = (type) => {
    setShowPowderCoatColors(type === "color");

    if (type === "standard") {
      // Set to no powder coat (standard black/chrome)
      selectOption({
        history,
        location,
        currentSelected: selected,
        option: powderCoatOption,
        item: { code: "X" },
      });
    }
  };

  return (
    <Box my="2rem">
      <H3>
        Base and Backbar Color{" "}
        <FA
          style={{ cursor: "pointer" }}
          icon={faQuestionCircle}
          onClick={() => setOpenDescription("powderCoat")}
        />
      </H3>

      <Formik
        initialValues={{
          powderCoatType: showPowderCoatColors ? "color" : "standard",
        }}
        validationSchema={powderCoatTypeSchema}
        onSubmit={() => null}
        component={() => (
          <Box mb="1rem">
            <PowderCoatTypeField
              name="powderCoatType"
              options={[
                {
                  id: "standard",
                  name: (
                    <span>
                      Standard Black / Chrome{" "}
                      <small style={{ fontStyle: "italic", fontSize: "0.9rem" }}>
                        (matching perf. package)
                      </small>
                    </span>
                  ),
                },
                {
                  id: "color",
                  name: (
                    <span>
                      Other Color{" "}
                      <small style={{ fontStyle: "italic", fontSize: "0.9rem" }}>
                        (will not appear in image)
                      </small>
                    </span>
                  ),
                },
              ]}
              label=""
              afterChange={(event) => {
                handlePowderCoatTypeChange(event.target.value);
              }}
            />
          </Box>
        )}
      />

      {showPowderCoatColors && (
        <>
          <ScrollStyle>
            <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow} onWheel={onWheel}>
              {powderCoatOptions
                .filter(({ code }) => code !== "X")
                .map((item) => (
                  <Item
                    key={item.code}
                    itemId={item.code}
                    option={powderCoatOption}
                    item={item}
                    selected={selected}
                  />
                ))}
            </ScrollMenu>
          </ScrollStyle>

          <UpholsteryLink
            onClick={() => setOpenPowderCoatExample(true)}
            style={{
              cursor: "pointer",
              marginLeft: 0,
              marginTop: "0.5rem",
              display: "inline-block",
            }}
          >
            <FA icon={faExternalLinkAlt} /> See examples
          </UpholsteryLink>

          <UpholsteryLink
            href={`https://www.biofit.com/contact?contact_category=rep&sku=${manufactureCode}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              marginLeft: "1rem",
              marginTop: "0.5rem",
              display: "inline-block",
            }}
          >
            <FA icon={faExternalLinkAlt} /> Custom colors available - contact us
          </UpholsteryLink>
        </>
      )}

      {openPowderCoatExample && (
        <Overlay onClose={() => setOpenPowderCoatExample(false)}>
          <OverlayContainer>
            <Flex justifyContent="flex-end">
              <Close onClick={() => setOpenPowderCoatExample(false)}>
                <SVGIcon icon="close" />
              </Close>
            </Flex>
            <div>
              <img
                alt="Powder Coat Examples"
                style={{ maxWidth: "100%", maxHeight: "80vh" }}
                src={powderCoatImage}
              />
            </div>
          </OverlayContainer>
        </Overlay>
      )}
    </Box>
  );
};

const Options = ({ selected }) => {
  const [openDescription, setOpenDescription] = useState();
  const closeDescription = () => setOpenDescription(null);

  // calculate options available within the selected market, style, series, and upholstered choices
  const { market, style, upholstered, series, seat, performance, back } = selected || {};

  const { options } = calculateAvailableOptions({
    market,
    style,
    upholstered,
    series,
    seat,
    back,
    performance,
  });

  return (
    <React.Fragment>
      {options.map((option) => (
        <Option
          key={option.type}
          option={option}
          selected={selected}
          setOpenDescription={setOpenDescription}
        />
      ))}
      <PowderCoatOptions
        selected={selected}
        options={options}
        setOpenDescription={setOpenDescription}
      />
      <SimpleOptions
        selected={selected}
        options={options}
        setOpenDescription={setOpenDescription}
      />
      {openDescription ? (
        <Overlay onClose={closeDescription}>
          <DescriptionList
            onClose={closeDescription}
            options={options}
            type={openDescription}
            selected={selected}
          />
        </Overlay>
      ) : null}
    </React.Fragment>
  );
};

export default Options;

function onWheel(apiObj, ev) {
  const isTouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15;

  if (isTouchpad) {
    ev.stopPropagation();
    return;
  }

  if (ev.deltaY < 0) {
    apiObj.scrollNext();
  } else if (ev.deltaY > 0) {
    apiObj.scrollPrev();
  }
}
